import { Component, HostListener, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';

type AlertType = 'success' | 'error' | 'warning' | 'info';

interface StructuredErrorItem {
    field?: string;
    message: string;
}

@Component({
    selector: 'app-alert-modal',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div
            class="modal-backdrop fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4 transition-opacity"
            (click)="close()"
        >
            <div
                class="modal-box relative bg-white dark:bg-[#111c38] p-6 sm:p-7 rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] border border-slate-100 dark:border-slate-800 w-full text-center transform transition-all"
                [ngClass]="hasErrors ? 'max-w-[460px]' : 'max-w-[360px]'"
                (click)="$event.stopPropagation()"
            >
                <!-- Close Button (X) -->
                <button
                    type="button"
                    class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    (click)="close()"
                    aria-label="Tutup"
                >
                    <img
                        src="assets/icons/icon-close.svg"
                        alt="close"
                        class="w-4 h-4 opacity-70 hover:opacity-100"
                    />
                </button>

                <!-- Icon & Content Container -->
                <div class="flex flex-col items-center">
                    <!-- Icon with gentle drop shadow -->
                    <div class="relative mb-3 flex items-center justify-center">
                        <img
                            [src]="icon"
                            class="w-16 h-16 sm:w-18 sm:h-18 drop-shadow-md select-none transform transition-transform duration-300 hover:scale-105"
                            [alt]="type"
                        />
                    </div>

                    <!-- Main Title -->
                    <h3
                        class="text-slate-900 dark:text-white font-bold leading-snug tracking-tight"
                        [ngClass]="
                            hasErrors
                                ? 'text-lg sm:text-xl mb-1'
                                : 'text-base sm:text-lg mb-2'
                        "
                    >
                        {{ displayTitle }}
                    </h3>

                    <!-- Subtitle when has validation errors -->
                    <p
                        *ngIf="hasErrors"
                        class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-4 font-normal"
                    >
                        Mohon periksa dan perbaiki kesalahan pengisian berikut:
                    </p>

                    <!-- Regular Message (When no error list is present) -->
                    <p
                        *ngIf="!hasErrors"
                        class="text-sm text-slate-600 dark:text-slate-300 font-normal leading-relaxed mb-6 whitespace-pre-line"
                    >
                        {{ message }}
                    </p>

                    <!-- Structured Errors Card List -->
                    <div
                        *ngIf="hasErrors"
                        class="w-full mb-5 text-left rounded-2xl bg-red-50/60 dark:bg-red-950/20 border border-red-200/70 dark:border-red-900/40 p-3 sm:p-3.5 max-h-56 overflow-y-auto space-y-2 error-scroll"
                    >
                        <div
                            *ngFor="let item of structuredErrors"
                            class="flex items-start gap-2.5 p-2.5 rounded-xl bg-white dark:bg-[#162347] border border-red-100 dark:border-red-900/30 shadow-xs"
                        >
                            <!-- Error Badge Icon -->
                            <div
                                class="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0 mt-0.5"
                            >
                                <svg
                                    class="w-3 h-3"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="2.5"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </div>

                            <!-- Error Message Details -->
                            <div class="flex-1 min-w-0">
                                <div
                                    *ngIf="item.field"
                                    class="inline-block text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wider bg-red-50 dark:bg-red-950/60 px-1.5 py-0.5 rounded-md mb-1"
                                >
                                    {{ item.field }}
                                </div>
                                <p
                                    class="text-xs text-slate-700 dark:text-slate-200 leading-relaxed font-medium break-words"
                                >
                                    {{ item.message }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Footer Action Button -->
                    <div class="flex justify-center w-full mt-1">
                        <button
                            type="button"
                            class="w-full py-2.5 px-6 rounded-xl sm:rounded-2xl bg-[#1B5091] hover:bg-[#15427b] text-white font-semibold text-sm transition-all shadow-md hover:shadow-lg active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2"
                            (click)="close()"
                        >
                            <span>{{
                                hasErrors ? 'Perbaiki Formulir' : 'OK'
                            }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            @keyframes modalPop {
                0% {
                    opacity: 0;
                    transform: scale(0.92) translateY(10px);
                }
                100% {
                    opacity: 1;
                    transform: scale(1) translateY(0);
                }
            }

            @keyframes backdropFade {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 1;
                }
            }

            .modal-backdrop {
                animation: backdropFade 0.2s cubic-bezier(0.16, 1, 0.3, 1)
                    forwards;
            }

            .modal-box {
                animation: modalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }

            .error-scroll {
                scrollbar-width: thin;
                scrollbar-color: rgba(239, 68, 68, 0.3) transparent;
            }

            .error-scroll::-webkit-scrollbar {
                width: 5px;
            }

            .error-scroll::-webkit-scrollbar-track {
                background: transparent;
            }

            .error-scroll::-webkit-scrollbar-thumb {
                background-color: rgba(239, 68, 68, 0.3);
                border-radius: 9999px;
            }

            .error-scroll::-webkit-scrollbar-thumb:hover {
                background-color: rgba(239, 68, 68, 0.5);
            }
        `,
    ],
})
export class AlertModalComponent {
    @Input() message: string = 'Berhasil!';
    @Input() type: AlertType = 'success';
    @Input() errors: any = null;

    private result$ = new Subject<void>();

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.close();
    }

    @HostListener('document:keydown.enter')
    onEnter(): void {
        this.close();
    }

    get displayTitle(): string {
        if (this.hasErrors && this.message) {
            const firstLine = this.message.split('\n')[0].trim();
            const clean = firstLine.endsWith(':')
                ? firstLine.slice(0, -1)
                : firstLine;
            if (clean.toLowerCase() === 'validation error') {
                return 'Validasi Gagal';
            }
            return clean;
        }
        return this.message;
    }

    get structuredErrors(): StructuredErrorItem[] {
        // 1. If explicit errors object/array is provided
        if (this.errors) {
            if (Array.isArray(this.errors)) {
                return this.errors
                    .filter((e) => !!e)
                    .map((e) => ({
                        message:
                            typeof e === 'object'
                                ? e.message || JSON.stringify(e)
                                : String(e),
                    }));
            }

            if (typeof this.errors === 'object') {
                const list: StructuredErrorItem[] = [];
                for (const key of Object.keys(this.errors)) {
                    const val = this.errors[key];
                    const fieldLabel = isNaN(Number(key))
                        ? this.formatFieldName(key)
                        : undefined;

                    if (Array.isArray(val)) {
                        val.forEach((item) => {
                            if (item) {
                                list.push({
                                    field: fieldLabel,
                                    message: String(item),
                                });
                            }
                        });
                    } else if (typeof val === 'string' && val.trim()) {
                        list.push({
                            field: fieldLabel,
                            message: val,
                        });
                    } else if (val && typeof val === 'object' && val.message) {
                        list.push({
                            field: fieldLabel,
                            message: val.message,
                        });
                    }
                }
                if (list.length > 0) {
                    return list;
                }
            }

            if (typeof this.errors === 'string' && this.errors.trim()) {
                return [{ message: this.errors }];
            }
        }

        // 2. Fallback: if message is formatted with newlines (e.g. "Validation Error:\nDurasi lembur...")
        if (typeof this.message === 'string' && this.message.includes('\n')) {
            const lines = this.message
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);

            if (lines.length > 1) {
                return lines.slice(1).map((l) => ({
                    message: l.replace(/^[-•*]\s*/, ''),
                }));
            }
        }

        return [];
    }

    get hasErrors(): boolean {
        return this.structuredErrors.length > 0;
    }

    formatFieldName(key: string): string {
        const dict: Record<string, string> = {
            total_hours: 'Durasi Lembur',
            end_time: 'Jam Selesai',
            start_time: 'Jam Mulai',
            overtime_date: 'Tanggal Lembur',
            original_day_off: 'Hari Libur Asal',
            day_off_to_swap: 'Hari Libur Pengganti',
            description: 'Keterangan',
            deliverables: 'Hasil Kerja',
            task_description: 'Uraian Tugas',
            reason: 'Alasan',
            notes: 'Catatan',
            user_id: 'Karyawan',
        };

        if (dict[key.toLowerCase()]) return dict[key.toLowerCase()];

        return key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    get icon() {
        switch (this.type) {
            case 'success':
                return 'assets/icons/success-icon.svg';
            case 'error':
                return 'assets/icons/error-icon.svg';
            case 'warning':
                return 'assets/icons/warning-icon.svg';
            case 'info':
                return 'assets/icons/info-icon.svg';
            default:
                return 'assets/icons/info-icon.svg';
        }
    }

    close() {
        this.result$.next();
        this.result$.complete();
    }

    getResult() {
        return this.result$.asObservable();
    }
}
